import React, { useState } from 'react';

// The main App component
export default function App() {
  // State variables to manage the app's data and UI
  const [theme, setTheme] = useState('');
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInitialMessage, setShowInitialMessage] = useState(true);

  // Function to handle the form submission
  const handleGenerateIdeas = async (e) => {
    e.preventDefault();
    if (!theme.trim()) return;

    // Reset UI state for a new request
    setLoading(true);
    setError(null);
    setIdeas([]);
    setShowInitialMessage(false);

    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

    if (!apiKey || apiKey === "YOUR_API_KEY_HERE") {
        setError("API Key is missing. Please create a .env.local file and add your REACT_APP_GEMINI_API_KEY.");
        setLoading(false);
        return;
    }

    try {
      // Define the structure for the AI's response
      const schema = {
        type: "ARRAY",
        items: {
          type: "OBJECT",
          properties: {
            projectName: { type: "STRING" },
            pitch: { type: "STRING" },
            features: { type: "ARRAY", items: { type: "STRING" } },
            techStack: { type: "STRING" }
          },
          required: ["projectName", "pitch", "features", "techStack"]
        }
      };

      const prompt = `You are an expert hackathon idea generator. Based on the theme "${theme}", create exactly 3 innovative project ideas. For each idea, provide a creative project name, a compelling one-sentence pitch, a list of 3-4 key features, and a suggested modern technology stack.`;

      const payload = {
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
          responseMimeType: "application/json",
          responseSchema: schema,
        }
      };
      
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(`API Error: ${response.status}. ${errorBody.error.message}`);
      }

      const result = await response.json();
      
      if (result.candidates && result.candidates.length > 0) {
        const ideasText = result.candidates[0].content.parts[0].text;
        const parsedIdeas = JSON.parse(ideasText);
        setIdeas(parsedIdeas);
      } else {
        throw new Error("Received an empty or invalid response from the API.");
      }

    } catch (err) {
      console.error("Error generating ideas:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
      <main className="w-full max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-400"><path d="M15.08 13.34c0-1.07.6-2.06 1.6-2.67a4.32 4.32 0 0 0-3.35-3.35c-1.01.2-1.99.78-2.67 1.6A4.32 4.32 0 0 0 8.34 15.08c.2 1.01.78 1.99 1.6 2.67a4.32 4.32 0 0 0 3.35 3.35c1.01-.2 1.99-.78 2.67-1.6.01-.01.01-.02.02-.03a4.32 4.32 0 0 0 2.27-2.81H17.5a4.5 4.5 0 0 1-2.42-1z"/><path d="M12 2v4"/><path d="m4.93 4.93 2.83 2.83"/><path d="M2 12h4"/><path d="m4.93 19.07 2.83-2.83"/><path d="M12 22v-4"/><path d="m19.07 19.07-2.83-2.83"/><path d="M22 12h-4"/><path d="m19.07 4.93-2.83 2.83"/></svg>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">Idea Oasis</h1>
          </div>
          <p className="text-lg text-gray-400">Your AI-powered spring of hackathon inspiration.</p>
        </div>

        {/* Input Form */}
        <form onSubmit={handleGenerateIdeas} className="mb-8">
          <div className="relative">
            <input 
              type="text" 
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="Enter a hackathon theme (e.g., 'Future of Education')" 
              className="w-full p-4 pr-32 bg-gray-800 border-2 border-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all duration-300" 
              required 
            />
            <button type="submit" disabled={loading} className="absolute top-1/2 right-2 -translate-y-1/2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-2 px-6 rounded-md transition-colors duration-300 flex items-center gap-2 disabled:bg-gray-500 disabled:cursor-not-allowed">
              {loading ? (
                <>
                  <span>Generating...</span>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </>
              ) : (
                <span>Generate</span>
              )}
            </button>
          </div>
        </form>

        {/* Results Section */}
        <div>
          {showInitialMessage && (
            <div className="text-center py-12 px-6 bg-gray-800/50 rounded-lg border border-dashed border-gray-700">
              <p className="text-gray-400">Your generated project ideas will appear here.</p>
            </div>
          )}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mb-4"></div>
              <p className="text-gray-300 text-lg">Brewing up some brilliant ideas... please wait.</p>
            </div>
          )}
          {error && (
            <div className="text-center py-12 px-6 bg-red-900/30 rounded-lg border border-red-500">
              <h3 className="text-red-400 text-xl font-semibold mb-2">Oops! Something went wrong.</h3>
              <p className="text-red-400">{error}</p>
            </div>
          )}
          {ideas.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ideas.map((idea, index) => (
                <IdeaCard key={index} idea={idea} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// A separate component for the idea card for better organization
const IdeaCard = ({ idea }) => {
  return (
    <div className="bg-gray-800 rounded-lg shadow-lg p-6 flex flex-col border border-gray-700 hover:border-cyan-500 transition-all duration-300 transform hover:-translate-y-1">
      <h3 className="text-xl font-bold text-cyan-400 mb-2">{idea.projectName}</h3>
      <p className="text-gray-300 mb-4 flex-grow">{idea.pitch}</p>
      
      <div className="mb-4">
        <h4 className="font-semibold text-gray-200 mb-2">Key Features:</h4>
        <ul className="list-disc list-inside text-gray-400 space-y-1">
          {idea.features.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-200 mb-2">Tech Stack:</h4>
        <p className="text-gray-400">{idea.techStack}</p>
      </div>
    </div>
  );
};
